import { useState, useCallback } from "react";
import { deleteRequest, getRequest, postRequest, putRequest } from "../utils/axiosClient";
import { errorToast } from "../utils/toast";
import { getAuthtoken } from "../utils/helper";

const formatErrorMessage = (message) => {
    if (typeof message === 'object' && message !== null) {
        return Object.keys(message).map((key) => `${key}: ${message[key]}`).join('\n');
    }
    return typeof message === 'string' ? message : 'An error occurred while processing your request';
};

// reads all complete SSE events from the stream; resolves with the last payload
const readSseStream = async ({ reader, onEvent }) => {
    const decoder = new TextDecoder();
    let buffer = '';
    let lastPayload = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) return lastPayload;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const block of events) {
            const line = block.trim();
            if (!line.startsWith('data:')) continue;
            try {
                const payload = JSON.parse(line.slice(5).trim());
                lastPayload = payload;
                onEvent?.(payload);
            } catch {
                // skip unparseable lines
            }
        }
    }
};

const useAxios = (defaultConfig) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleRequest = useCallback(async (requestFn, url, body = null, config = {}) => {
        setIsLoading(true);
        setError(null);
        // merged here so the catch honours showErrorToast from either the per-call or the hook-level config
        const mergedConfig = { ...config, ...defaultConfig };
        try {
            const response = await requestFn({ url, data: body, config: mergedConfig });
            return response.data;
        } catch (err) {
            const responseData = err?.response?.data || {};
            const errorMessage = responseData?.message || err.message || 'Internal server error';
            const formattedMessage = formatErrorMessage(errorMessage);

            setError(formattedMessage);

            if (mergedConfig.showErrorToast) {
                errorToast(formattedMessage);
            }

            return { success: false, message: errorMessage, statusCode: responseData?.statusCode, data: responseData?.data };
        } finally {
            setIsLoading(false);
        }
    }, [defaultConfig]);

    // streams an SSE POST; calls onEvent(payload) for each event; resolves/rejects when stream closes
    const streamPost = useCallback(async (url, data, { onEvent } = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetch(`${defaultConfig?.baseURL ?? ''}${url}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    Authorization: `Bearer ${getAuthtoken()}`,
                },
                body: JSON.stringify(data),
            });

            if (!res.ok) {
                const body = await res.json();
                throw new Error(body?.message || `Request failed with status ${res.status}`);
            }

            const lastPayload = await readSseStream({ reader: res.body.getReader(), onEvent });

            if (lastPayload?.success === false) {
                throw new Error(lastPayload?.message || 'stream failed');
            }
            return lastPayload;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [defaultConfig]);

    const getData = useCallback((url, config) => handleRequest(getRequest, url, config), [handleRequest]);
    const postData = useCallback((url, data, config) => handleRequest(postRequest, url, data, config), [handleRequest]);
    const putData = useCallback((url, data, config) => handleRequest(putRequest, url, data, config), [handleRequest]);
    const deleteData = useCallback((url, data, config) => handleRequest(deleteRequest, url, data, config), [handleRequest]);

    return {
        isLoading,
        error,
        getData,
        postData,
        putData,
        deleteData,
        setIsLoading,
        streamPost,
    };
};

export default useAxios;
